import { AltoSpinningLoader, Body, MdSpacing } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { loginSucceeded, logoutSucceeded } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { BACKEND_ENVIRONMENT, GROWTHBOOK_API_KEY, WEB_VERSION } from '~shared/config';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { getAnalyticalProperties } from '~shared/lib/experiments/src/getAnalyticalProperties';
import { Sentry } from '~shared/sentry';
import { configureStore } from './configureStore';
import PatientDashboardApp from './features/app/components/PatientDashboardApp';
import './main.scss';
import 'swiper/css';

// extend console logs to also print to cypress in integration env
if (BACKEND_ENVIRONMENT === 'integration') {
  // eslint-disable-next-line no-console
  const originalConsoleLog = console.log;
  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;

  // @ts-expect-error TS(7006): Parameter 'level' implicitly has an 'any' type.
  const logToCypress = (level, args) => {
    // @ts-expect-error TS(2304): we're intentionally excluding Cypress types since they conflict with Jest. Can leave this for now.
    Cypress?.log({
      name: 'consoleRedirect',
      displayName: level,
      message: args,
      consoleProps: () => ({
        args,
      }),
    });
  };

  // eslint-disable-next-line no-console
  console.log = (...args) => {
    logToCypress('console.log', args);
    originalConsoleLog.apply(console, args);
  };

  console.warn = (...args) => {
    logToCypress('console.warn', args);
    originalConsoleWarn.apply(console, args);
  };

  console.error = (...args) => {
    logToCypress('console.error', args);
    originalConsoleError.apply(console, args);
  };
}

Sentry.init({
  dsn: 'https://c1be7b1e99af4b8194beb242c2cc32e2@o4505590845734912.ingest.sentry.io/4506079326568450',
  release: WEB_VERSION,
  environment: BACKEND_ENVIRONMENT,
  // Filter out errors thrown by browser extensions. These are unhandled rejections captured by
  // Sentry's global handler but originate from extension code running in the page context, not
  // from the app itself. Common examples: runtime.sendMessage when a tab no longer exists.
  ignoreErrors: [
    'Invalid call to runtime.sendMessage()',
    /^Non-Error promise rejection captured with value/,
    // CustomEvent thrown as unhandled rejection — originates from browser extensions,
    // not from app code.
    /^Event `CustomEvent` \(type=unhandledrejection\)/,
    // Empty object thrown as unhandled rejection — third-party scripts (e.g. Segment).
    /^Object captured as promise rejection with keys: \[object has no keys\]/,
  ],
  denyUrls: [
    // Chrome/Safari extensions
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
    // YouTube player widget — crashes internally when iframe is removed from DOM
    // https://alto-pharmacy.sentry.io/issues/6805960705/events/7c31ad1d184142da8b90ef87c3b5795b/
    /www\.youtube\.com\/s\/player/i,
  ],
  // add trace headers to localhost, alto API requests, and other requests on this domain, but not to auth0's login.*.alto endpoint
  tracePropagationTargets: ['localhost', /patients\.(prod|stg|dev).alto\./, /^\//],
  integrations: [
    Sentry.browserTracingIntegration({
      // v8+ replacement for `beforeNavigate`. Remap pathnames so transactions group intelligently —
      // e.g. /begin/asdfgh and /begin/123456 will both be grouped as /begin/:temp-password,
      // and numeric IDs in /orders/:id and /checkout/success/:id are collapsed.
      beforeStartSpan: (options) => {
        return {
          ...options,
          name: window.location.pathname
            .replace(/\/begin\/[^/]+$/, '/begin/:temp-password')
            .replace(/\/start\/[^/]+$/, '/start/:temp-password')
            .replace(/\/status\/[^/]+$/, '/status/:temp-password')
            .replace(/\/onboard\/verify\/[^/]+$/, '/onboard/verify/:temp-password')
            .replace(/\/orders\/\d+/, '/orders/:id')
            .replace(/\/checkout\/success\/\d+/, '/checkout/success/:id')
            .replace(/\/rate-shipment\/[^/]+$/, '/rate-shipment/:hash')
            .replace(/\/subscriptions\/[^/]+$/, '/subscriptions/:hash'),
        };
      },
    }),
    Sentry.browserProfilingIntegration(),
  ],
  // trace 100% of user traffic
  tracesSampleRate: 1.0,
  profilesSampleRate: 1.0,
  normalizeDepth: 10, // cut off annotated redux state at 10 levels deep
});

const { store } = configureStore();

const NO_ENDLESS_LOADING_SCREEN = 1000 * 60;
let loaderTimeout: NodeJS.Timeout;

function renderApp(Component = PatientDashboardApp, skipInitialDataFetch = false) {
  if (loaderTimeout) clearTimeout(loaderTimeout);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!);
  root.render(
    <Component
      store={store}
      skipInitialDataFetch={skipInitialDataFetch}
    />,
  );
}

if (module.hot) {
  module.hot.accept('./features/app/components/PatientDashboardApp', () => {
    renderApp(PatientDashboardApp);
  });
}

function renderLoader() {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!);
  loaderTimeout = setTimeout(() => {
    root.render(
      <>
        <AltoSpinningLoader />
        <MdSpacing />
        <Body center>It looks like something went wrong. Refresh the page and try again?</Body>
      </>,
    );
    const isOffline = !navigator.onLine;
    // @ts-expect-error navigator.connection is not in all TS lib types
    const connectionType = navigator.connection?.effectiveType;

    // Only treat as warning if the user is fully offline
    Sentry.captureMessage(`App load timeout: ${NO_ENDLESS_LOADING_SCREEN}ms`, (scope) => {
      scope.setLevel(isOffline ? 'warning' : 'error');
      scope.setContext('load timeout', {
        timeoutMs: NO_ENDLESS_LOADING_SCREEN,
        pathname: window.location.pathname,
        isOffline,
        connectionType,
      });
      return scope;
    });
  }, NO_ENDLESS_LOADING_SCREEN);

  root.render(<AltoSpinningLoader />);
}

Experimentation.init(GROWTHBOOK_API_KEY, (featureKey, result) =>
  store.dispatch(
    // @ts-expect-error Argument of type '(dispatch: Dispatch<AnyAction>) => void' is not assignable to parameter of type 'AnyAction'.
    sendAnalyticsEvent(
      createEvent(EVENTS.EXPERIMENT_FEATURE_DECISION, {
        source: 'growthbook',
        experiment_key: featureKey,
        variation_key: result.value,
        reason_variation_key: result.source,
        ...getAnalyticalProperties(store.getState()),
      }),
    ),
  ),
);

// if receive unauthorized request logout and don't call load
const searchParams = new URLSearchParams(window.location.search);
const isKnownUnauthorized = searchParams.get('authorized') === '0';

// We don't want to authenticate when on the landing or csat pages
const webLandingPage =
  window?.location?.pathname?.startsWith('/start/') || window?.location?.pathname?.startsWith('/begin/');
const csatPage = window?.location?.pathname?.startsWith('/csat/');

// if we can go a short amount of time without showing a loader, do that. This
// prevents a flash of the loader when we don't need it.
const initialLoaderDelay = 500;
const showLoaderTimeout = setTimeout(() => {
  renderLoader();
}, initialLoaderDelay);

if (isKnownUnauthorized) {
  clearTimeout(showLoaderTimeout);
  store.dispatch(logoutSucceeded());
  renderApp();
} else if (webLandingPage || csatPage) {
  clearTimeout(showLoaderTimeout);

  // Render the app, but don't fetch initial data.
  renderApp(PatientDashboardApp, true);
} else {
  // fetchUsers and the result is used to determine whether you are actually
  // logged in. Ideally we would use the persisted storage, but we need to wait
  // until the rehydration happens. We should upgrade to redux-persist v5, which
  // supports this out of the box.
  // @ts-expect-error action type mismatch, can dispatch fetchUsers
  // eslint-disable-next-line import/no-deprecated, promise/catch-or-return
  store.dispatch(fetchUsers()).then((success: boolean) => {
    clearTimeout(showLoaderTimeout);

    // eslint-disable-next-line promise/always-return
    if (success) {
      const newState = store.getState();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const user = getCurrentUser(newState)!;
      store.dispatch(loginSucceeded(user));
    } else {
      store.dispatch(logoutSucceeded());
    }

    renderApp();
  });
}
